import {
  DateRange,
  DayPickerProps,
  DayPicker as DefaultDayPicker,
} from '@daypicker/react';
import { token } from '@porsche-code/kado-styled-system/tokens';
import {
  PInputText,
  PInputTextProps,
} from '@porsche-design-system/components-react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useEffect, useMemo, useState } from 'react';

import '@daypicker/react/style.css';

import { Popover } from './Popover';

dayjs.extend(customParseFormat);

export type DayPickerInputProps = {
  name: string;
  format: string;
  required?: boolean;
  disabled?: boolean;
  min?: Date;
  max?: Date;
  selected?: Date | string | null;
  onSelect: (selected: Date | undefined) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  onBlur?: () => void;
} & Pick<PInputTextProps, 'label' | 'state' | 'message'>;

const styles = {
  day: {
    fontSize: '15px',
  },
  month: {
    fontSize: '13px',
  },
  dropdowns: {
    paddingLeft: token('spacing.md'),
  },
  root: {
    '--rdp-day-width': '36px',
    '--rdp-day-height': '36px',
    '--rdp-day_button-border-radius': token('radii.full'),
    '--rdp-accent-color': token('colors.text.primary'),
    '--rdp-selected-border': `1px solid ${token('colors.border.contrastLower')}`,
    '--rdp-range_start-color': token('colors.text.canvas'),
    '--rdp-range_end-color': token('colors.text.canvas'),
    '--rdp-range_start-background-color': token('colors.background.frosted'),
    '--rdp-range_middle-background-color': token('colors.background.frosted'),
    '--rdp-today-color': token('colors.text.info'),
  } as React.CSSProperties,
};

const DayPickerInput = ({
  inputRef,
  label,
  name,
  format,
  required,
  min,
  max,
  disabled,
  selected,
  onSelect,
  state,
  message,
  onBlur,
}: DayPickerInputProps) => {
  const selectedDate = useMemo(() => {
    // useMemo is necessary to avoid creating a new instance of selectedDate on every render
    // which makes clearing the input field impossible PAIM-3259
    return typeof selected === 'string'
      ? dayjs(selected, format).toDate()
      : selected;
  }, [format, selected]);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>(
    selectedDate ? dayjs(selectedDate).format(format) : '',
  );

  useEffect(() => {
    setInputValue(selectedDate ? dayjs(selectedDate).format(format) : '');
  }, [selectedDate, format]);

  return (
    <Popover
      open={isOpen}
      onChange={setIsOpen}
      placement="bottom-start"
      withPortal
      style={{ display: 'block' }}
      target={
        <PInputText
          label={label}
          aria-label={label}
          state={state}
          message={message}
          autoComplete="off"
          ref={inputRef as unknown as React.Ref<HTMLInputElement>}
          required={required}
          disabled={disabled}
          name={name}
          value={inputValue}
          placeholder={format}
          onInput={(e) => {
            const { value } = e.target as HTMLInputElement;
            setInputValue(value);
            const date = dayjs(value, format, true);
            onSelect(date.isValid() ? date.toDate() : undefined);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => onBlur?.()}
        />
      }
    >
      <DayPicker
        mode="single"
        styles={styles}
        selected={selectedDate ?? undefined}
        defaultMonth={selectedDate ?? undefined}
        startMonth={min ?? new Date(2000, 0)}
        endMonth={max ?? new Date(2099, 0)}
        onSelect={(date: Date | undefined) => {
          onSelect(date);
          setInputValue(dayjs(date).format(format));
          setIsOpen(false);
        }}
      />
    </Popover>
  );
};

const DayPicker = (props: DayPickerProps) => (
  <DefaultDayPicker
    styles={styles}
    weekStartsOn={1}
    captionLayout="dropdown"
    startMonth={new Date(2000, 0)}
    endMonth={new Date(2099, 0)}
    {...props}
  />
);

export { DayPicker, DayPickerInput };
export type { DateRange };
